/**
 * Pure helpers behind the cash-receipts list page: the URL search params →
 * list-query mapping, and the task-assignee filter's options. Kept out of the
 * page component so the branches are unit-testable without a DOM.
 */
import type { useGT } from 'gt-react';
import { z } from 'zod';
import {
  CASH_RECEIPT_LIFECYCLE_STATUSES,
  CASH_RECEIPT_TASK_UNASSIGNED,
  COMPLETED_CASH_RECEIPT_LIFECYCLE_STATUSES,
  CashReceiptSortBySchema,
  OPEN_CASH_RECEIPT_LIFECYCLE_STATUSES,
  thenSortSchema,
} from '@erp/rpc';
import type {
  CashReceiptLifecycleStatus,
  ListCashReceiptsInput,
} from '@erp/rpc';
import type { MultiSelectOption } from '@/components/common/MultiSelect';

export type CashReceiptsStatusTab = 'open' | 'completed' | 'all';

/**
 * The tab as the URL and saved views carry it. Reads the tab names from
 * before lifecycle statuses (`needs_review` → Open, `matched` → Completed) so
 * old links and saved views still load.
 */
export const CashReceiptsStatusTabSchema = z
  .enum(['open', 'completed', 'all', 'needs_review', 'matched'])
  .transform((tab): CashReceiptsStatusTab => {
    if (tab === 'needs_review') return 'open';
    if (tab === 'matched') return 'completed';
    return tab;
  });

/**
 * The sort column as the URL and saved views carry it. Drops `status`, which
 * the removed Status column sorted by, so an old saved sort doesn't keep
 * ordering the list by a column that is no longer shown.
 */
export const CashReceiptsSortBySearchSchema = CashReceiptSortBySchema.transform(
  (sortBy) => (sortBy === 'status' ? undefined : sortBy),
);

/** The columns the table sorts by: the contract's, minus the retired `status`. */
export const CashReceiptsTableSortBySchema = CashReceiptSortBySchema.exclude([
  'status',
]);
export type CashReceiptsTableSortBy = z.infer<
  typeof CashReceiptsTableSortBySchema
>;

/** The sort's tie-breaker levels as the URL and saved views carry them. */
export const CashReceiptsThenSortSearchSchema = thenSortSchema(
  CashReceiptsTableSortBySchema,
);

/** The lifecycle statuses a tab holds — the Status filter's options there. */
export function lifecycleStatusesForTab(
  tab: CashReceiptsStatusTab,
): readonly CashReceiptLifecycleStatus[] {
  switch (tab) {
    case 'open':
      return OPEN_CASH_RECEIPT_LIFECYCLE_STATUSES;
    case 'completed':
      return COMPLETED_CASH_RECEIPT_LIFECYCLE_STATUSES;
    case 'all':
      return CASH_RECEIPT_LIFECYCLE_STATUSES;
  }
}

/**
 * The Status filter after switching to `tab`: selections the tab can't show
 * are dropped, so the filter never silently empties the list. Undefined when
 * nothing is left.
 */
export function lifecycleFilterForTab(
  selected: readonly CashReceiptLifecycleStatus[] | undefined,
  tab: CashReceiptsStatusTab,
): CashReceiptLifecycleStatus[] | undefined {
  const inTab = new Set(lifecycleStatusesForTab(tab));
  const kept = selected?.filter((status) => inTab.has(status)) ?? [];
  return kept.length > 0 ? kept : undefined;
}

/** The page's filter/sort search params (the route's validated subset). */
export type CashReceiptsListSearch = Pick<
  ListCashReceiptsInput,
  | 'paymentDateFrom'
  | 'paymentDateTo'
  | 'customerId'
  | 'paymentType'
  | 'paymentAmountMin'
  | 'paymentAmountMax'
  | 'search'
  | 'lifecycleStatus'
  | 'taskStatus'
  | 'taskAssignee'
  | 'sortBy'
  | 'sortDirection'
  | 'thenSort'
>;

/** A multi-select param, or undefined when nothing is selected. */
function nonEmpty<T>(values: T[] | undefined): T[] | undefined {
  return values && values.length > 0 ? values : undefined;
}

/**
 * The list query for the current view. Absent date bounds fall back to the
 * page's rolling default window; empty multi-selects and a blank search are
 * omitted rather than sent as "match nothing". Statuses the active tab can't
 * hold (e.g. a shared link with `statusTab=open&lifecycleStatus=keyed`) are
 * dropped, matching the Status filter's options for that tab.
 */
export function buildCashReceiptListQuery(
  search: CashReceiptsListSearch,
  view: {
    activeTab: CashReceiptsStatusTab;
    pageIndex: number;
    pageSize: number;
    /** yyyy-MM-dd bounds used when the URL has none. */
    defaultDateRange: { from: string; to: string };
  },
): ListCashReceiptsInput {
  return {
    queue: view.activeTab === 'all' ? undefined : view.activeTab,
    lifecycleStatus: lifecycleFilterForTab(
      search.lifecycleStatus,
      view.activeTab,
    ),
    paymentDateFrom: search.paymentDateFrom ?? view.defaultDateRange.from,
    paymentDateTo: search.paymentDateTo ?? view.defaultDateRange.to,
    customerId: nonEmpty(search.customerId),
    paymentType: nonEmpty(search.paymentType),
    paymentAmountMin: search.paymentAmountMin,
    paymentAmountMax: search.paymentAmountMax,
    search: search.search || undefined,
    taskStatus: nonEmpty(search.taskStatus),
    taskAssignee: nonEmpty(search.taskAssignee),
    sortBy: search.sortBy,
    sortDirection: search.sortDirection,
    thenSort: search.thenSort,
    limit: view.pageSize,
    offset: view.pageIndex * view.pageSize,
  };
}

type Translate = ReturnType<typeof useGT>;

/**
 * Options for the task-assignee filter, in the task queue's token format:
 * "Me" first, then "Unassigned", the rest of the org's users, then agents
 * (the keying agent works these tasks). Pass no agents when the viewer may
 * not see them.
 */
export function buildTaskAssigneeFilterOptions({
  gt,
  currentUserId,
  users,
  agents,
}: {
  gt: Translate;
  currentUserId: string | undefined;
  users: ReadonlyArray<{ id: string; name: string }>;
  agents: ReadonlyArray<{ id: string; name: string }>;
}): MultiSelectOption[] {
  const usersGroup = gt('Users', {
    context: 'group label in the cash receipts task assignee filter',
  });
  const agentsGroup = gt('Agents', {
    context: 'group label in the cash receipts task assignee filter',
  });
  const me: MultiSelectOption[] = currentUserId
    ? [
        {
          id: `user:${currentUserId}`,
          label: gt('Me', {
            context: 'cash receipts task assignee filter option, current user',
          }),
          group: usersGroup,
        },
      ]
    : [];
  return [
    ...me,
    {
      id: CASH_RECEIPT_TASK_UNASSIGNED,
      label: gt('Unassigned', {
        context: 'cash receipts task assignee filter option, no assignee',
      }),
      group: usersGroup,
    },
    ...users.flatMap((user) =>
      user.id === currentUserId
        ? []
        : [{ id: `user:${user.id}`, label: user.name, group: usersGroup }],
    ),
    ...agents.map((agent) => ({
      id: `agent:${agent.id}`,
      label: agent.name,
      group: agentsGroup,
    })),
  ];
}

/**
 * The "Assigned to Me" quick toggle: on exactly when the assignee filter is
 * the viewer's own token and nothing else; `next` is the filter a click
 * applies (clear when on, just-me when off). Null without a signed-in user.
 */
export function assignedToMeToggle(
  taskAssignee: readonly string[] | undefined,
  currentUserId: string | undefined,
): { active: boolean; next: string[] | undefined } | null {
  if (!currentUserId) return null;
  const token = `user:${currentUserId}`;
  const active = taskAssignee?.length === 1 && taskAssignee[0] === token;
  return { active, next: active ? undefined : [token] };
}
